// components/MeetingDetail.jsx

import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

function Badge({ type, label }) { return <span className={`badge badge-${type}`}>{label}</span>; }
function fmt(d) { if (!d) return '—'; return new Date(d).toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' }); }

function ProcessingView({ meeting, onRefresh }) {
  useEffect(() => {
    if (meeting.status === 'done' || meeting.status === 'error') return;
    const t = setInterval(async () => {
      const s = await api.getStatus(meeting.id);
      if (s.status === 'done' || s.status === 'error') { clearInterval(t); onRefresh(); }
    }, 2000);
    return () => clearInterval(t);
  }, [meeting.id, meeting.status, onRefresh]);

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center', maxWidth: 420 }}>
        <div style={{ fontSize: 48, marginBottom: '1.25rem' }}>{meeting.status === 'error' ? '❌' : '⚙️'}</div>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: '0.5rem' }}>
          {meeting.status === 'transcribing' && 'Transcribing audio...'}
          {meeting.status === 'analysing' && 'Extracting meeting intelligence...'}
          {meeting.status === 'pending' && 'Queued for processing...'}
          {meeting.status === 'error' && 'Processing failed'}
        </div>
        {meeting.status === 'error'
          ? <div className="text-red text-sm mt-sm">{meeting.error_msg}</div>
          : <>
              <div className="text-muted text-sm mb-md">Powered by MeetingDebt AI</div>
              <div className="processing-bar"><div className="processing-bar-fill" /></div>
            </>}
      </div>
    </div>
  );
}

function EmailModal({ meetingId, onClose }) {
  const [emails, setEmails] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const send = async () => {
    const list = emails.split(',').map(e => e.trim()).filter(Boolean);
    if (!list.length) return setError('Enter at least one email');
    setSending(true);
    try {
      const r = await api.sendEmail(meetingId, list);
      if (r.skipped) setError('Email not configured — add EMAIL_USER + EMAIL_PASS to backend .env');
      else setSent(true);
    } catch (e) { setError(e.message); }
    finally { setSending(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Email Summary</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {sent ? (
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <div style={{ fontSize: 40, marginBottom: '1rem' }}>✅</div>
              <div style={{ fontWeight: 600 }}>Summary sent!</div>
            </div>
          ) : (
            <>
              <label className="text-xs text-dim text-mono" style={{ display:'block', marginBottom:8, textTransform:'uppercase', letterSpacing:'0.08em' }}>Recipients</label>
              <input type="text" placeholder="alice@co.com, bob@co.com" value={emails} onChange={e => setEmails(e.target.value)} style={{ marginBottom: 12 }} />
              {error && <div className="text-red text-sm" style={{ marginBottom: 12 }}>⚠ {error}</div>}
            </>
          )}
        </div>
        {!sent && (
          <div className="modal-footer">
            <button className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={send} disabled={sending}>
              {sending ? '⟳ Sending...' : '→ Send'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ActionItemsTable({ meetingId, items, onUpdate }) {
  const [updating, setUpdating] = useState(null);
  const toggle = async (item) => {
    setUpdating(item.id);
    try { await api.updateActionItem(meetingId, item.id, { status: item.status === 'done' ? 'open' : 'done' }); onUpdate(); }
    finally { setUpdating(null); }
  };
  if (!items?.length) return <div className="empty-state" style={{ padding: '2rem' }}><div className="empty-state-icon">✅</div><div className="empty-state-title">No action items</div></div>;
  return (
    <table>
      <thead><tr><th style={{ width:40 }}></th><th>Task</th><th>Owner</th><th>Deadline</th><th>Priority</th><th>Status</th></tr></thead>
      <tbody>
        {items.map(item => (
          <tr key={item.id} style={{ opacity: item.status === 'done' ? 0.45 : 1 }}>
            <td><button className="btn btn-ghost btn-sm" style={{ padding:'2px 6px', fontSize:14 }} disabled={updating===item.id} onClick={() => toggle(item)}>{item.status==='done'?'✓':'○'}</button></td>
            <td style={{ textDecoration: item.status==='done'?'line-through':'none' }}>{item.description}</td>
            <td className="text-muted text-sm">{item.owner??'—'}</td>
            <td style={{ fontFamily:'var(--mono)', fontSize:11, color:'var(--text-2)' }}>{item.deadline??'—'}</td>
            <td><Badge type={item.priority} label={item.priority} /></td>
            <td><Badge type={item.status==='done'?'done-item':'open'} label={item.status} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SimpleList({ items, icon, color, renderItem }) {
  if (!items?.length) return <div className="empty-state" style={{ padding:'2rem' }}><div className="empty-state-icon">{icon}</div><div className="empty-state-title">None found</div></div>;
  return <div>{items.map((item, i) => (
    <div key={item.id} style={{ display:'flex', gap:'0.75rem', padding:'0.85rem 1.25rem', borderBottom: i<items.length-1?'1px solid var(--border)':'none' }}>
      <span style={{ color, fontSize:14, flexShrink:0, marginTop:2 }}>{icon}</span>
      <div style={{ flex:1 }}>{renderItem(item)}</div>
    </div>
  ))}</div>;
}

export default function MeetingDetail({ meetingId, onDelete }) {
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTranscript, setShowTranscript] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [activeTab, setActiveTab] = useState('actions');

  const load = useCallback(async () => {
    try { setMeeting(await api.getMeeting(meetingId)); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [meetingId]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this meeting?')) return;
    await api.deleteMeeting(meetingId); onDelete();
  };

  if (loading) return <div className="page" style={{ color:'var(--text-3)', fontFamily:'var(--mono)', paddingTop:'4rem' }}>Loading...</div>;
  if (!meeting) return null;
  if (meeting.status !== 'done' && meeting.status !== 'error') return <ProcessingView meeting={meeting} onRefresh={load} />;

  return (
    <div className="page fade-in">
      {/* Header */}
      <div className="page-header">
        <div style={{ flex:1, minWidth:0 }}>
          <div className="page-title" style={{ marginBottom:6 }}>{meeting.title}</div>
          <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', flexWrap:'wrap' }}>
            <span className="text-sm text-muted">{fmt(meeting.recorded_at)}</span>
            <Badge type={meeting.status} label={meeting.status} />
            {meeting.speakers?.length > 0 && (
              <span className="text-xs text-dim">👥 {meeting.speakers.join(', ')}</span>
            )}
          </div>
        </div>
        <div className="flex gap-sm" style={{ flexShrink:0 }}>
          {meeting.transcript && <button className="btn btn-outline btn-sm" onClick={() => setShowTranscript(true)}>Transcript</button>}
          {meeting.status === 'done' && <>
            <button className="btn btn-outline btn-sm" onClick={() => setShowEmail(true)}>✉ Email</button>
            <button className="btn btn-outline btn-sm" onClick={async () => {
              try {
                const res = await fetch(api.getPdfUrl(meetingId), {
                  headers: { 'x-user-id': window.__clerk_user_id || 'default' }
                });
                if (!res.ok) {
                  const err = await res.json();
                  if (err.error === 'upgrade_required') { alert('PDF export is a Pro feature. Upgrade to unlock it.'); return; }
                  throw new Error(err.error || 'Failed');
                }
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = meeting.title.replace(/[^a-z0-9]/gi,'_') + '_summary.pdf';
                a.click();
                URL.revokeObjectURL(url);
              } catch(e) { alert('PDF export failed: ' + e.message); }
            }}>↓ PDF</button>
          </>}
          <button className="btn btn-danger btn-sm" onClick={handleDelete}>Delete</button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid mb-lg">
        {[['Action Items', meeting.action_items?.length??0,'var(--accent)'],['Decisions',meeting.decisions?.length??0,'var(--blue)'],['Blockers',meeting.blockers?.length??0,'var(--red)'],['Open Questions',meeting.open_questions?.length??0,'var(--yellow)']].map(([label,val,color]) => (
          <div key={label} className="stat-card">
            <div className="stat-value" style={{ color }}>{val}</div>
            <div className="stat-label">{label}</div>
          </div>
        ))}
      </div>

      {/* Summary */}
      {meeting.summary && (
        <div className="card mb-lg">
          <div className="card-header"><span className="card-title">📄 Summary</span></div>
          <div className="card-body"><p style={{ fontSize:14, lineHeight:1.75 }}>{meeting.summary}</p></div>
        </div>
      )}

      {meeting.status === 'error' && (
        <div style={{ background:'var(--red-dim)', border:'1px solid rgba(255,68,85,0.3)', borderRadius:'var(--radius)', padding:'0.75rem 1rem', marginBottom:'1rem', color:'var(--red)', fontSize:13 }}>
          ⚠ {meeting.error_msg}
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        {[['actions',`Actions (${meeting.action_items?.length??0})`],['decisions',`Decisions (${meeting.decisions?.length??0})`],['blockers',`Blockers (${meeting.blockers?.length??0})`],['questions',`Questions (${meeting.open_questions?.length??0})`]].map(([key,label]) => (
          <button key={key} className={`tab ${activeTab===key?'active':''}`} onClick={() => setActiveTab(key)}>{label}</button>
        ))}
      </div>

      <div className="card">
        <div style={{ padding:0 }}>
          {activeTab==='actions' && <ActionItemsTable meetingId={meetingId} items={meeting.action_items} onUpdate={load} />}
          {activeTab==='decisions' && <SimpleList items={meeting.decisions} icon="✓" color="var(--accent)"
            renderItem={d => <><div style={{ fontSize:13 }}>{d.description}</div>{d.made_by&&<div className="text-xs text-dim mt-xs">by {d.made_by}</div>}</>} />}
          {activeTab==='blockers' && <SimpleList items={meeting.blockers} icon="⚠" color="var(--red)"
            renderItem={b => <><div style={{ fontSize:13 }}>{b.description}</div>{b.owner&&<div className="text-xs text-dim mt-xs">{b.owner}</div>}</>} />}
          {activeTab==='questions' && <SimpleList items={meeting.open_questions} icon="?" color="var(--blue)"
            renderItem={q => <><div style={{ fontSize:13 }}>{q.question}</div>{q.asked_by&&<div className="text-xs text-dim mt-xs">Asked by {q.asked_by}</div>}</>} />}
        </div>
      </div>

      {showTranscript && (
        <div className="modal-backdrop" onClick={() => setShowTranscript(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">Full Transcript</div><button className="btn btn-ghost btn-sm" onClick={() => setShowTranscript(false)}>✕</button></div>
            <div className="modal-body"><pre style={{ whiteSpace:'pre-wrap', fontFamily:'var(--mono)', fontSize:11, lineHeight:1.8, color:'var(--text-2)' }}>{meeting.transcript}</pre></div>
          </div>
        </div>
      )}
      {showEmail && <EmailModal meetingId={meetingId} onClose={() => setShowEmail(false)} />}
    </div>
  );
}
